/**
 * adapters/http/errorCodeMap — mapeia status HTTP (e falhas de rede) para os
 * códigos do Catálogo de Erros do cliente (seção 12 do design, WEB-ERR-001..013).
 *
 * Nenhuma mensagem aqui expõe PII, token ou stack (RNF 5.4, 6.2, Req 26.5).
 * As mensagens pt-BR vivem em `shared/i18n` (chave `error.<code>`).
 */

/** Códigos canônicos do catálogo de erros do cliente. */
export const WEB_ERROR_CODES = [
  "WEB-ERR-001",
  "WEB-ERR-002",
  "WEB-ERR-003",
  "WEB-ERR-004",
  "WEB-ERR-005",
  "WEB-ERR-006",
  "WEB-ERR-007",
  "WEB-ERR-008",
  "WEB-ERR-009",
  "WEB-ERR-010",
  "WEB-ERR-011",
  "WEB-ERR-012",
  "WEB-ERR-013",
] as const;

export type WebErrorCode = (typeof WEB_ERROR_CODES)[number];

/** Estado de sessão expirada (401 de sessão). Distinto de erro de UI comum. */
export const SESSION_EXPIRED = "expired" as const;
export type SessionExpired = typeof SESSION_EXPIRED;

/** Contexto da requisição para desambiguar mapeamentos com mesmo status. */
export type ErrorContext = "login" | "session" | "invite" | "stage" | "export" | "generic";

/** Status sentinela para falha de rede/timeout (sem resposta HTTP). */
export const NETWORK_ERROR_STATUS = 0;

/**
 * Mapeia um status HTTP (ou `0` para rede/timeout) para o código do catálogo.
 * `context` desambigua casos como 401 de login (WEB-ERR-001) vs 401 de sessão
 * (`expired`/WEB-ERR-002) e 422 de estágio (WEB-ERR-007/008) vs genérico (013).
 */
export function mapStatusToErrorCode(
  status: number,
  context: ErrorContext = "generic",
): WebErrorCode | SessionExpired {
  // Rede / timeout (sem resposta).
  if (status === NETWORK_ERROR_STATUS) return "WEB-ERR-010";

  // 5xx — indisponibilidade de servidor.
  if (status >= 500) {
    if (status === 501) return "WEB-ERR-011"; // não implementado (gateway parcial)
    if (context === "export") return "WEB-ERR-012";
    return "WEB-ERR-009";
  }

  switch (status) {
    case 401:
      return context === "login" ? "WEB-ERR-001" : SESSION_EXPIRED;
    case 403:
      return "WEB-ERR-003";
    case 400:
    case 410:
      if (context === "invite") return "WEB-ERR-004";
      return "WEB-ERR-013";
    case 404:
      return "WEB-ERR-011";
    case 409:
      return context === "stage" ? "WEB-ERR-007" : "WEB-ERR-013";
    case 422:
      if (context === "stage") return "WEB-ERR-008";
      if (context === "export") return "WEB-ERR-012";
      return "WEB-ERR-013";
    default:
      if (context === "export") return "WEB-ERR-012";
      return "WEB-ERR-013";
  }
}
